import { useState, useEffect, useMemo } from 'react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import {
  LabelElement,
  BarcodeElementProps,
  QRCodeElementProps,
  isBarcodeElement,
  isQRCodeElement,
} from '../types';
import { getResolvedDisplayValue } from '../helpers';
import { SalesOrder } from 'services/salesOrders';

interface UseElementImageGeneratorProps {
  element: LabelElement;
  previewMode: boolean;
  previewData?: SalesOrder | null;
}

interface UseElementImageGeneratorReturn {
  barcodeImageUrl: string | null;
  qrImageUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

export const useElementImageGenerator = ({
  element,
  previewMode,
  previewData,
}: UseElementImageGeneratorProps): UseElementImageGeneratorReturn => {
  const [barcodeImageUrl, setBarcodeImageUrl] = useState<string | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const canvas = useMemo(() => document.createElement('canvas'), []);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const generateImages = async () => {
      const valueToGenerate = getResolvedDisplayValue(
        element,
        previewMode,
        previewData ?? null
      );

      if (
        !valueToGenerate &&
        (isBarcodeElement(element) || isQRCodeElement(element))
      ) {
        // If value is empty, don't try to generate, clear existing images
        if (isMounted) {
          setBarcodeImageUrl(null);
          setQrImageUrl(null);
          setIsLoading(false);
        }
        return;
      }

      // Barcode generation
      if (isBarcodeElement(element)) {
        try {
          JsBarcode(canvas, valueToGenerate || 'Sample', {
            format: (element as BarcodeElementProps).barcodeType || 'CODE128',
            width: 2,
            height: Math.max(
              10,
              element.height - ((element as BarcodeElementProps).text ? 20 : 0)
            ),
            displayValue: false, // Renderer handles text display
            text: undefined,
            margin: 0,
          });
          if (isMounted) {
            setBarcodeImageUrl(canvas.toDataURL());
          }
        } catch (err) {
          console.error('Error generating barcode:', err);
          if (isMounted) setError(err as Error);
        }
      }

      // QR code generation
      if (isQRCodeElement(element)) {
        const qrElement = element as QRCodeElementProps;
        try {
          const size = Math.min(element.width, element.height);
          const dataUrl = await QRCode.toDataURL(
            valueToGenerate || 'Sample QR',
            {
              width: Math.max(10, size),
              margin: 0,
              color: {
                dark: qrElement.fill || '#000000',
                light: '#ffffff',
              },
            }
          );
          if (isMounted) {
            setQrImageUrl(dataUrl);
          }
        } catch (err) {
          console.error('Error generating QR code:', err);
          if (isMounted) setError(err as Error);
        }
      }

      if (isMounted) setIsLoading(false);
    };

    if (isBarcodeElement(element) || isQRCodeElement(element)) {
      generateImages();
    }

    return () => {
      isMounted = false;
    };
  }, [element, previewMode, previewData, element.width, element.height]); // Added element.width and element.height for QR size updates

  return { barcodeImageUrl, qrImageUrl, isLoading, error };
};
